import { useToast } from '@chakra-ui/react'
import { DatePickerInput } from '@mantine/dates'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import Table from '../../../../../../../components/Table/Table'
import { useGetFinanstilsynetSamplingListQuery } from '../../../../../../../redux/services/coreApi'
import { setFinanstilsynetSamplingTableState } from '../../../../../../../redux/slices/tableFiltersSlice'
import type { RootState } from '../../../../../../../redux/store'
import downloadCsv from '../../../../../../../utils/downloadCsv'
import {
  DEFAULT_RANGE,
  type FinanstilsynetDateRange,
  rangeToCsvQuery,
  rangeToFilters,
} from '../dateRange'

const dkkFormatter = new Intl.NumberFormat('da-DK', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const SamplingListTab: React.FC = () => {
  const toast = useToast()
  const [isExporting, setIsExporting] = useState(false)
  const [range, setRange] = useState<FinanstilsynetDateRange>(DEFAULT_RANGE)

  const sessionToken = useSelector((state: RootState) => state.session.token)
  const tableState = useSelector(
    (state: RootState) => state.finanstilsynetSamplingTable
  )

  const { data: rows, isLoading } = useGetFinanstilsynetSamplingListQuery(
    rangeToFilters(range)
  )

  const handleExport = async () => {
    if (!sessionToken) {
      toast({
        title: 'Ikke logget ind',
        description: 'Log ind igen for at downloade CSV-filen.',
        status: 'error',
      })
      return
    }
    setIsExporting(true)
    try {
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      await downloadCsv(
        `/compliance/finanstilsynet/sampling-list.csv${rangeToCsvQuery(range)}`,
        `penning-stikproveliste-${timestamp}.csv`,
        sessionToken
      )
    } catch (err) {
      toast({
        title: 'Kunne ikke eksportere CSV',
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div className="max-w-3xl text-sm text-brand-light-text-muted">
          Kundeliste til brug for udtagelse af stikprøver. Indeholder
          kundenummer, navn, aktivitet samt sum og antal transaktioner i DKK
          for kunder og aktivitet i den valgte periode. CPR-numre og
          fødselsdatoer indgår ikke.
        </div>
        <button
          type="button"
          className="button"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? 'Eksporterer…' : 'Eksportér CSV'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium">Periode (inkl. slutdato)</label>
        <DatePickerInput
          type="range"
          value={range}
          onChange={setRange}
          valueFormat="D MMM YYYY"
          allowSingleDateInRange
          clearable={false}
          classNames={{
            month: 'w-auto',
            input:
              '!rounded-lg !border-none !shadow-sm hover:bg-brand-gray-100 !px-3 !py-2.5 !text-sm !font-medium !flex !justify-between !items-center',
          }}
        />
        <span className="text-xs text-brand-light-text-muted">
          Begge datoer er inkluderet i udtrækket.
        </span>
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm">
        <strong>Bemærk:</strong> Tabellen herunder er kun en forhåndsvisning og
        viser ikke nødvendigvis alle kolonner og værdier. Den fulde og
        autoritative indberetning til Finanstilsynet — afgrænset til den valgte
        periode — findes ved at trykke på "Eksportér CSV".
      </div>

      <Table
        tableState={tableState}
        setTableState={setFinanstilsynetSamplingTableState}
        navigate={() => null}
        onClickRow={() => null}
        isLoading={isLoading}
        emptyDataRender={<div className="text-xl mb-3">Ingen data</div>}
        defaultData={(rows ?? []).map(r => ({ ...r, id: r.customerNumber }))}
        schema={[
          {
            header: 'Kundenummer',
            accessorKey: 'customerNumber',
            type: 'string',
            priority: 1,
          },
          {
            cardTitle: true,
            header: 'Navn',
            accessorKey: 'name',
            type: 'string',
            priority: 2,
          },
          {
            header: 'Aktivitet',
            accessorKey: 'offeredActivity',
            type: 'string',
            priority: 3,
          },
          {
            header: 'Sum DKK',
            accessorKey: 'transactionSumDkk',
            type: 'number',
            priority: 4,
            cellValueWrapper: value => (
              <div>{dkkFormatter.format(Number(value) || 0)}</div>
            ),
          },
          {
            header: 'Antal transaktioner',
            accessorKey: 'transactionCountDkk',
            type: 'number',
            priority: 5,
          },
          {
            header: 'Status',
            accessorKey: 'status',
            type: 'status',
            priority: 6,
          },
        ]}
      />
    </div>
  )
}

export default SamplingListTab
